import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    EmploymentSetting,
    EmploymentSettings,
    employmentSettingsSchema,
    SetLocation,
    setLocationSchema,
    teacherAccountState,
    TeacherAccountState_v2,
    teacherProfileSchema,
    teacherTypeData,
    teacherWorkingHistory,
    TeacherWorkingHistory,
    TeacherProfile,
    SupportStaffProfile,
    supportStaffProfileSchema,
    EducatorProfile,
    educatorProfileSchema,
    Languages,
    languagesSchema,
} from '../firebase/types-teacher'
import { LiveData, LiveDataStatus, setLiveData } from '../firebase/types'
import {
    AccreditationDocument_v2,
    accreditationDocumentSchema,
    accreditationDocumentsSchema,
    LocalTeacherEmploymentType,
    TeacherEmploymentSetting,
} from './types'
import { RootState } from './store'
import {
    TeacherNames,
    TeacherContact,
    WorkingRights,
    workingRightsSchema,
    namesSchema,
    contactSchema,
    TeacherProfilePicture,
    TeacherReference,
    TeacherEmergencyContacts,
    IdentificationDocuments,
    identificationDocumentsSchema,
    FieldState_v2,
    IdentificationDocument,
    TeacherServices,
    workingRightsDataSchema,
    profilePictureDataSchema,
    referenceDataSchema,
    emergencyContactsDataSchema,
    servicesDataSchema,
    WorkingRightsType,
    TeacherService,
} from '../schemas/teacherReg'
import z, { prettifyError, ZodError, ZodType } from 'zod'
import { getCombinedState_v2 } from '../pages/Dashboard/ReviewPage/helper'
import { AusState } from '../firebase/accreditation-api'
import { accreditationSort_v2 } from '../pages/Dashboard/helper'
import dayjs from '../helpers/dayjs'
import { createKey } from '../hooks/useAccreditationConfig'
import { getError } from '../helpers/utils'
import { logger } from '../helpers/logger'

type TeacherDataState = {
    accountState: LiveData<TeacherAccountState_v2>
    accreditation: LiveData<Record<string, AccreditationDocument_v2>>
    setLocation: LiveData<SetLocation>
    employmentSettings: LiveData<EmploymentSettings>
    workingRights: LiveData<WorkingRights>

    names: LiveData<TeacherNames>
    contact: LiveData<TeacherContact>
    profilePicture: LiveData<TeacherProfilePicture>
    identity: LiveData<IdentificationDocuments>
    reference: LiveData<TeacherReference>

    emergencyContacts: LiveData<TeacherEmergencyContacts>
    service: LiveData<TeacherServices>

    workingHistory: LiveData<TeacherWorkingHistory>
    type: LiveData<z.infer<typeof teacherTypeData>>

    profile: LiveData<TeacherProfile>
    supportStaffProfile: LiveData<SupportStaffProfile>
    educatorProfile: LiveData<EducatorProfile>
    languages: LiveData<Languages>

    dateApproved: LiveData<string | null>
}

type ITeacherSlice = {
    data: TeacherDataState
    onboardingChecks: {
        accreditationBySetting: Record<string, boolean>
        accreditationRequiredDocIdsBySetting: Record<
            string,
            Record<string, boolean>
        >
        accreditationAllReady: boolean
    }
}

export type TeacherDataKey = keyof TeacherDataState

const initialState: ITeacherSlice = {
    data: {
        accountState: { status: 'default', data: undefined },
        setLocation: { status: 'default', data: undefined },
        employmentSettings: { status: 'default', data: undefined },
        accreditation: { status: 'default', data: undefined },

        names: { status: 'default', data: undefined },
        contact: { status: 'default', data: undefined },
        workingRights: { status: 'default', data: undefined },

        identity: { status: 'default', data: undefined },
        profilePicture: { status: 'default', data: undefined },

        reference: { status: 'default', data: undefined },
        emergencyContacts: { status: 'default', data: undefined },
        service: { status: 'default', data: undefined },

        workingHistory: { status: 'default', data: undefined },

        type: { status: 'default', data: undefined },

        profile: { status: 'default', data: undefined },
        supportStaffProfile: { status: 'default', data: undefined },
        educatorProfile: { status: 'default', data: undefined },

        languages: { status: 'default', data: undefined },

        dateApproved: { status: 'default', data: undefined },
    },
    onboardingChecks: {
        accreditationBySetting: {},
        accreditationRequiredDocIdsBySetting: {},
        accreditationAllReady: false,
    },
}

const schemaMap: Partial<Record<TeacherDataKey, ZodType<any>>> = {
    accountState: teacherAccountState,
    accreditation: accreditationDocumentsSchema,
    setLocation: setLocationSchema,
    employmentSettings: employmentSettingsSchema,

    names: namesSchema,
    contact: contactSchema,
    workingRights: workingRightsDataSchema,

    identity: identificationDocumentsSchema,
    profilePicture: profilePictureDataSchema,

    reference: referenceDataSchema,
    emergencyContacts: emergencyContactsDataSchema,

    service: servicesDataSchema,
    workingHistory: teacherWorkingHistory,
    type: teacherTypeData,

    profile: teacherProfileSchema.nullable().default(null),
    supportStaffProfile: supportStaffProfileSchema.nullable().default(null),
    educatorProfile: educatorProfileSchema.nullable().default(null),

    languages: languagesSchema.nullable().default(null),

    dateApproved: z.string().default(''),
}

export const teacherSlice = createSlice({
    name: 'teacher',
    initialState,
    reducers: {
        setTeacherDefault: () => initialState,
        setLoading: (state, action: PayloadAction<TeacherDataKey>) => {
            ;(state.data as Record<TeacherDataKey, LiveData<any>>)[
                action.payload
            ] = {
                status: 'loading',
                data: undefined,
            }
        },
        setData: (
            state,
            action: PayloadAction<{
                key: TeacherDataKey
                value: unknown
                schema?: ZodType<any>
            }>
        ) => {
            const { key, value, schema } = action.payload
            const resolvedSchema = schema || schemaMap[key]

            if (!resolvedSchema) {
                ;(state.data as Record<TeacherDataKey, LiveData<any>>)[key] = {
                    status: 'error',
                    error: `No schema provided for key "${key}"`,
                }
                return
            }

            ;(state.data as Record<TeacherDataKey, LiveData<any>>)[key] =
                setLiveData(value, resolvedSchema)
        },

        //#region Backward-Compatible Convenience Actions
        setAccountState: (state, action: PayloadAction<unknown>) => {
            state.data.accountState = setLiveData(
                action.payload,
                teacherAccountState
            )
        },
        setSetLocation: (state, action: PayloadAction<unknown>) => {
            state.data.setLocation = setLiveData(
                action.payload,
                setLocationSchema
            )
        },
        setEmploymentSettings: (state, action: PayloadAction<unknown>) => {
            state.data.employmentSettings = setLiveData(
                action.payload,
                employmentSettingsSchema
            )
        },
        setWorkingRights: (state, action: PayloadAction<unknown>) => {
            state.data.workingRights = setLiveData(
                action.payload,
                workingRightsSchema
            )
        },

        //#endregion

        //#region Accreditation Actions
        setAccreditationSnapshot: (
            state,
            action: PayloadAction<
                Record<string, AccreditationDocument_v2> | null | undefined
            >
        ) => {
            const payload = action.payload
            if (payload === undefined) {
                state.data.accreditation = {
                    status: 'default',
                    data: undefined,
                }
                return
            }
            try {
                state.data.accreditation = {
                    status: 'success',
                    data:
                        payload === null
                            ? {}
                            : accreditationDocumentsSchema.parse(payload),
                }
            } catch (e) {
                state.data.accreditation = {
                    status: 'error',
                    error:
                        e instanceof ZodError ? prettifyError(e) : getError(e),
                }
            }
        },
        upsertAccreditationDoc: (
            state,
            action: PayloadAction<{
                id: string
                doc: AccreditationDocument_v2 | null | undefined
            }>
        ) => {
            const { id, doc } = action.payload

            if (
                state.data.accreditation.status !== 'success' ||
                !state.data.accreditation.data
            ) {
                state.data.accreditation = { status: 'success', data: {} }
            }
            if (!doc) {
                delete state.data.accreditation.data?.[id]
                return
            }
            const parsedDoc = accreditationDocumentSchema.safeParse(doc)
            if (parsedDoc.success) {
                state.data.accreditation.data![id] = parsedDoc.data
            } else {
                logger.warn(
                    `Failed to parse accreditation doc ${id}: ${prettifyError(parsedDoc.error)}`
                )
            }
        },
        removeAccreditationDoc: (state, action: PayloadAction<string>) => {
            if (
                state.data.accreditation.status !== 'success' ||
                !state.data.accreditation.data
            ) {
                state.data.accreditation = { status: 'success', data: {} }
                return
            }
            delete state.data.accreditation.data[action.payload]
        },
        setAccreditationCheckState: (
            state,
            action: PayloadAction<{
                key: string
                ready: boolean
                docIds: Record<string, boolean>
            }>
        ) => {
            state.onboardingChecks.accreditationBySetting[action.payload.key] =
                action.payload.ready
            state.onboardingChecks.accreditationRequiredDocIdsBySetting[
                action.payload.key
            ] = action.payload.docIds

            const checks = Object.values(
                state.onboardingChecks.accreditationBySetting
            )
            state.onboardingChecks.accreditationAllReady =
                checks.length > 0 && checks.every(Boolean)
        },
        syncAccreditationSettings: (
            state,
            action: PayloadAction<{ activeKeys: string[] }>
        ) => {
            const active = new Set(action.payload.activeKeys)

            for (const key of Object.keys(
                state.onboardingChecks.accreditationBySetting
            )) {
                if (!active.has(key)) {
                    delete state.onboardingChecks.accreditationBySetting[key]
                }
            }

            for (const key of Object.keys(
                state.onboardingChecks.accreditationRequiredDocIdsBySetting
            )) {
                if (!active.has(key)) {
                    delete state.onboardingChecks
                        .accreditationRequiredDocIdsBySetting[key]
                }
            }

            const checks = Object.values(
                state.onboardingChecks.accreditationBySetting
            )
            state.onboardingChecks.accreditationAllReady =
                checks.length > 0 && checks.every(Boolean)
        },
        resetAccreditationCheckState: (state) => {
            state.onboardingChecks = {
                accreditationBySetting: {},
                accreditationRequiredDocIdsBySetting: {},
                accreditationAllReady: false,
            }
        },
        //#endregion
    },
})

export const selectTeacherAccountState = (state: RootState) =>
    state.teacher.data.accountState
export const selectTeacherDateApproved = (state: RootState) =>
    state.teacher.data.dateApproved
export const selectTeacherSetLocation = (state: RootState) =>
    state.teacher.data.setLocation
export const selectTeacherEmploymentSettings = (state: RootState) =>
    state.teacher.data.employmentSettings
export const selectTeacherType = (state: RootState) => state.teacher.data.type

export const selectDerivedIncompleteTeacherEmploymentSetting = createSelector(
    [selectTeacherEmploymentSettings],
    (data): LocalTeacherEmploymentType | undefined | null => {
        if (data.status !== 'success') return undefined

        let isCrt: boolean = false
        let isEducator: boolean = false
        let isEs: boolean = false

        Object.values(data.data).forEach((el) => {
            switch (el.type) {
                case 'school': {
                    isCrt = true
                    break
                }
                case 'earlyChildcare': {
                    isEducator = true
                    break
                }
                case 'supportStaff': {
                    isEs = true
                    break
                }
            }
        })

        if (isEs) return 'supportStaff'
        if (isCrt && isEducator) return 'both'
        if (isEducator) return 'earlyChildcare'
        if (isCrt) return 'school'
        return null
    }
)

export const selectDerivedTeacherEmploymentSetting = createSelector(
    [selectTeacherEmploymentSettings],
    (data): LocalTeacherEmploymentType | undefined | null => {
        if (data.status !== 'success') return undefined

        let isCrt: boolean = false
        let isEducator: boolean = false
        let isEs: boolean = false

        Object.values(data.data).forEach((el) => {
            if (el.state !== 'approved') return
            switch (el.type) {
                case 'school': {
                    isCrt = true
                    break
                }
                case 'earlyChildcare': {
                    isEducator = true
                    break
                }
                case 'supportStaff': {
                    isEs = true
                    break
                }
            }
        })

        if (isEs) return 'supportStaff'
        if (isCrt && isEducator) return 'both'
        if (isEducator) return 'earlyChildcare'
        if (isCrt) return 'school'
        return null
    }
)

export const selectTeacherAccreditationLive = (state: RootState) =>
    state.teacher.data.accreditation
export const selectTeacherWorkingRights = (state: RootState) =>
    state.teacher.data.workingRights
export const selectTeacherAccreditationChecks = (state: RootState) =>
    state.teacher.onboardingChecks
export const selectTeacherReference = (state: RootState) =>
    state.teacher.data.reference
export const selectTeacherServices = (state: RootState) =>
    state.teacher.data.service
export const selectTeacherWorkingHistory = (state: RootState) =>
    state.teacher.data.workingHistory

export const selectIncompleteAccreditationDocIds = createSelector(
    [selectTeacherAccreditationLive],
    (data) => {
        if (data.status !== 'success') return []
        return Object.keys(data.data).filter(
            (k) => data.data[k].state === 'incomplete'
        )
    }
)

export const selectTeacherAccreditationDocumentByTypes = (type: string[]) =>
    createSelector(
        [selectTeacherAccreditationLive],
        (
            accreds
        ):
            | { loading: true; data?: never }
            | {
                  loading: false
                  data: AccreditationDocument_v2 | undefined
              } => {
            if (accreds.status !== 'success') return { loading: true }
            return {
                loading: false,
                data: Object.values(accreds.data)
                    .filter((el) => type.includes(el.accreditationType))
                    .sort(accreditationSort_v2)?.[0],
            }
        }
    )

export const selectRequiresAccreditationLevel = (state: RootState): boolean =>
    'NSW:school' in state.teacher.onboardingChecks.accreditationBySetting

export const selectTeacherLatestService = (
    state: RootState
): [string, TeacherService] | null | undefined => {
    if (state.teacher.data.service.status !== 'success') return undefined
    if (Object.keys(state.teacher.data.service.data).length === 0) return null
    return Object.entries(state.teacher.data.service.data).sort(
        (a, b) => b[1].timeUploaded - a[1].timeUploaded
    )[0]
}

export const selectTeacherRequiredAccreditationDocIdsBySetting = (
    state: RootState
) => state.teacher.onboardingChecks.accreditationRequiredDocIdsBySetting

export const selectEmploymentSettingIds = (state: RootState) => {
    const data = state.teacher.data.employmentSettings
    if (data.status !== 'success') return undefined

    return Object.keys(data.data).sort()
}

export const selectRequiredAccreditationDocIdsForSetting = (
    settingKey: string
) =>
    createSelector(
        [selectTeacherRequiredAccreditationDocIdsBySetting],
        (bySetting) => bySetting[settingKey] || []
    )

export const accreditationDocDefault: AccreditationDocument_v2 = {
    accreditationType: '',
    expiryDate: '',
    registrationDate: '',
    dateSubmitted: '',
    imageURL: '',
    reference: '',
    type: '',
    state: 'incomplete',
    docTitle: '',
    qualification: '',
    classification: '',
    courseType: '',
}

export const selectAccreditationDocsForKey = (settingKey: string) =>
    createSelector(
        [
            selectRequiredAccreditationDocIdsForSetting(settingKey),
            selectTeacherAccreditationLive,
        ],
        (
            docIds,
            accreditationLive
        ):
            | {
                  loading: false
                  state: FieldState_v2
                  docs: {
                      type: string
                      docId: string | undefined
                      doc: AccreditationDocument_v2
                  }[]
                  requiredDocIds: string[]
                  allTypes: Set<string>
              }
            | { loading: true } => {
            if (
                accreditationLive.status !== 'success' ||
                !accreditationLive.data
            )
                return { loading: true }

            const docs = Object.entries(accreditationLive.data || {})

            const docIdKeys = Object.keys(docIds)
            const requiredDocIds = docIdKeys.filter((k) => !docIds[k])

            const config: {
                type: string
                docId: string | undefined
                doc: AccreditationDocument_v2
            }[] = docIdKeys.map((type) => {
                const doc = docs.find(
                    (doc) => doc[1].accreditationType === type
                )

                return {
                    type,
                    ...(doc
                        ? {
                              docId: doc[0],
                              doc: doc[1],
                          }
                        : {
                              docId: undefined,
                              doc: accreditationDocDefault,
                          }),
                }
            })

            return {
                loading: false,
                state: getCombinedState_v2(
                    requiredDocIds.map((_, i) => config[i].doc.state)
                ),
                requiredDocIds: requiredDocIds,
                docs: config,
                allTypes: new Set<string>(docIdKeys),
            }

            // return docs.filter(
            //     (doc) =>
            //         !!doc.accreditationType &&
            //         requiredDocIds.includes(doc.accreditationType)
            // )
        }
    )

export const selectTeacherExpiredAccreditationDocumentCount = createSelector(
    [selectTeacherAccreditationLive],
    (
        data
    ): { loading: true; count?: never } | { loading: false; count: number } => {
        if (data.status !== 'success') return { loading: true }

        return {
            loading: false,
            count: Object.values(data.data).filter(
                (el) =>
                    el.state === 'approved' &&
                    el.expiryDate &&
                    dayjs().isAfter(dayjs(el.expiryDate), 'date')
            ).length,
        }
    }
)

export const selectTeacherNames = (state: RootState) => state.teacher.data.names
export const selectTeacherContact = (state: RootState) =>
    state.teacher.data.contact

type DerivedState =
    | {
          loading: false
          state: FieldState_v2
          itemStates: Record<string, FieldState_v2>
          //   ready: Record<string, boolean>

          values: any
      }
    | { loading: true; values?: never; itemStates?: never; state?: never }

export const selectTeacherNamesState = createSelector(
    [selectTeacherNames],
    (names) => {
        if (names.status === 'success') {
            return getCombinedState_v2([
                names.data.firstName.state,
                names.data.lastName.state,
            ])
        } else {
            return undefined
        }
    }
)

export const selectTeacherContactState = createSelector(
    [selectTeacherContact],
    (names) => {
        if (names.status === 'success') {
            return getCombinedState_v2([
                names.data.email.state,
                names.data.contact.state,
                names.data.homeLocation.state,
            ])
        } else {
            return undefined
        }
    }
)

export const selectDerivedBasicDetailsState = createSelector(
    [selectTeacherNames, selectTeacherContact],
    (namesData, contactData): DerivedState => {
        if (
            namesData.status === 'success' &&
            contactData.status === 'success'
        ) {
            const { firstName, lastName, firstNameActual } = namesData.data
            const { contact, email, homeLocation } = contactData.data

            const namesState = getCombinedState_v2([
                firstName.state,
                lastName.state,
                // firstNameActual.state,
            ])
            const contactState = getCombinedState_v2([
                contact.state,
                email.state,
                homeLocation.state,
            ])

            const state = getCombinedState_v2([namesState, contactState])

            return {
                loading: false,
                state: state,
                itemStates: {
                    names: namesState,
                    contact: contactState,
                },
                values: {
                    firstName: firstName.value,
                    lastName: lastName.value,
                    firstNameActual: firstNameActual.value,

                    contact: contact.value,
                    email: email.value,
                    homeLocation: homeLocation.address,
                },
            }
        } else {
            return {
                loading: true,
            }
        }
    }
)

export const selectTeacherProfilePicture = (state: RootState) =>
    state.teacher.data.profilePicture
export const selectDerivedProfilePhotoState = createSelector(
    [selectTeacherProfilePicture],
    (data): DerivedState => {
        if (data.status === 'success') {
            return {
                loading: false,
                state: data.data.state,
                itemStates: {},
                values: data.data,
            }
        } else {
            return { loading: true }
        }
    }
)

export const selectDerivedIdentityState = createSelector(
    [
        (state: RootState) => state.teacher.data.identity,
        selectTeacherWorkingRights,
        selectTeacherAccountState,
    ],
    (identityData, workingRightsData, accountStateData): DerivedState => {
        if (
            identityData.status !== 'success' ||
            workingRightsData.status !== 'success' ||
            accountStateData.status !== 'success'
        ) {
            return { loading: true }
        }

        const docKeys = Object.keys(identityData.data || {})
        // const identityDocs = Object.values(identityData.data || {})
        const workingRights = workingRightsData.data
        const accountState = accountStateData.data
        const forceSubmitted = accountState === 'submitted'

        type IdentityDoc =
            | { id: string | undefined; data: Partial<IdentificationDocument> }
            | undefined

        const defaultDoc: IdentityDoc = {
            id: undefined,
            data: {
                state: 'incomplete',
                value: null,
                birthDate: null,
            },
        }

        let primary: IdentityDoc
        let secondary: IdentityDoc
        let photoHoldingId: IdentityDoc

        for (const key of docKeys) {
            const doc: IdentificationDocument | undefined =
                identityData.data?.[key]
            const normalizedDoc: Partial<IdentificationDocument> = {
                ...doc,
                state: forceSubmitted ? 'submitted' : doc.state,
            }

            if (doc.documentType === 'photoHolidingIdentification') {
                photoHoldingId = { id: key, data: normalizedDoc }
                continue
            }

            if (workingRights.value === 'temporaryVisaHolder') {
                if (doc.documentType === 'passport') {
                    primary = { id: key, data: normalizedDoc }
                } else if (!secondary) {
                    secondary = { id: key, data: normalizedDoc }
                }
                continue
            }

            const isPrimaryCandidate =
                doc.documentType === 'passport' ||
                doc.documentType === 'birthCertificate' ||
                doc.documentType === 'australianCitizenshipCertificate'

            if (!primary && isPrimaryCandidate) {
                primary = { id: key, data: normalizedDoc }
            } else if (!secondary) {
                secondary = { id: key, data: normalizedDoc }
            }
        }

        const primaryState = primary?.data?.state || 'incomplete'
        const secondaryState = secondary?.data?.state || 'incomplete'
        const photoHoldingState = photoHoldingId?.data?.state || 'incomplete'

        return {
            loading: false,
            state: getCombinedState_v2([
                primaryState,
                secondaryState,
                photoHoldingState,
            ]),
            itemStates: {
                primary: primaryState,
                secondary: secondaryState,
                photoHoldingId: photoHoldingState,
            },
            values: {
                primary: primary || defaultDoc,
                secondary: secondary || defaultDoc,
                photoHoldingId: photoHoldingId || defaultDoc,
            },
        }
    }
)

export const selectTeacherDob = createSelector(
    [selectDerivedIdentityState],
    (docs) => {
        if (docs.loading) return undefined
        return (
            (docs.values.primary.data as Partial<IdentificationDocument>)
                ?.birthDate || null
        )
    }
)

export const selectTeacherPrimaryIdentityId = createSelector(
    [selectDerivedIdentityState],
    (
        data
    ):
        | { loading: true; data?: never }
        | { loading: false; data: string | undefined } => {
        if (data.loading || data.values.primary.data.type === 'passport')
            return { loading: true }

        return { loading: false, data: data.values.primary.id }
    }
)
export const selectTeacherPrimaryIdentity = createSelector(
    [selectDerivedIdentityState],
    (
        data
    ):
        | { loading: true; document?: never }
        | {
              loading: false
              document: {
                  id: string | undefined
                  data: IdentificationDocument
              }
          } => {
        if (data.loading) return { loading: true }

        return { loading: false, document: data.values.primary }
    }
)

export const selectTeacherSecondaryIdentity = createSelector(
    [selectDerivedIdentityState],
    (
        data
    ):
        | { loading: true; document?: never }
        | {
              loading: false
              document: {
                  id: string | undefined
                  data: IdentificationDocument
              }
          } => {
        if (data.loading) return { loading: true }

        return { loading: false, document: data.values.secondary }
    }
)

const selectOnboardingAccreditationBySetting = (state: RootState) =>
    state.teacher.onboardingChecks.accreditationBySetting
export const selectOnboardingAccreditationBySettingByKey =
    (setting: EmploymentSetting) =>
    (state: RootState): boolean =>
        Boolean(
            state.teacher.onboardingChecks.accreditationBySetting[
                createKey(setting)
            ]
        )
export const selectOnboardingSettings = createSelector(
    [selectOnboardingAccreditationBySetting],
    (data) => Object.keys(data)
)

export const selectDerivedAccreditationState = createSelector(
    [selectTeacherAccreditationLive, selectTeacherAccountState],
    (accreditationData, accountStateData): DerivedState => {
        if (
            accreditationData.status !== 'success' ||
            accountStateData.status !== 'success'
        ) {
            return { loading: true }
        }

        const forceSubmitted = accountStateData.data === 'submitted'
        const docs = Object.entries(accreditationData.data || {}).map(
            ([id, doc]) => ({
                id,
                ...doc,
                state: (forceSubmitted
                    ? 'submitted'
                    : doc.state || 'incomplete') as FieldState_v2,
            })
        )

        const itemStates = docs.reduce<Record<string, FieldState_v2>>(
            (acc, doc) => {
                const key = doc.accreditationType || doc.id || 'unknown'
                acc[key] = (doc.state || 'incomplete') as FieldState_v2
                return acc
            },
            {}
        )

        const state = getCombinedState_v2(
            Object.values(itemStates).length > 0
                ? Object.values(itemStates)
                : ['incomplete']
        )

        return {
            loading: false,
            state,
            itemStates,
            values: {
                docs,
                byId: accreditationData.data || {},
            },
        }
    }
)
const selectTeacherEmergencyContacts = (state: RootState) =>
    state.teacher.data.emergencyContacts
export const selectDerivedEmergencyContactState = createSelector(
    [selectTeacherEmergencyContacts],
    (data): DerivedState => {
        if (data.status !== 'success') {
            return {
                loading: true,
            }
        }

        return {
            loading: false,
            state: getCombinedState_v2([
                data.data.primary.state,
                data.data.secondary.state,
            ]),
            itemStates: {
                primary: data.data.primary.state,
                secondary: data.data.secondary.state,
            },
            values: {
                primary: data.data.primary,
                secondary: data.data.secondary,
            },
        }
    }
)

export const selectEmploymentSettingId = createSelector(
    [
        selectTeacherEmploymentSettings,
        (
            _,
            homeState: AusState,
            setting: TeacherEmploymentSetting
        ): [AusState, TeacherEmploymentSetting] => [homeState, setting],
    ],
    (settings, [homeState, setting]) => {
        if (settings.status !== 'success') return undefined
        return Object.entries(settings.data).find(
            ([_, v]) => v.locationState === homeState && v.type === setting
        )?.[0]
    }
)
export const selectCanRemoveSetting = createSelector(
    [selectTeacherEmploymentSettings],
    (settings) =>
        settings.status === 'success' && Object.keys(settings.data).length > 1
)

export const selectAccreditationTypes = createSelector(
    [selectRequiredAccreditationDocIdsForSetting],
    (data) =>
        Object.values(data).reduce<Set<string>>((a, c) => {
            Object.keys(c).forEach((el) => a.add(el))
            return a
        }, new Set<string>())
    // (data) => Object.values(data).reduce((a, c) => a.puObject.keys(c)}) , new Set())
)

export const makeSelectAccreditationByType = () =>
    createSelector(
        [selectTeacherAccreditationLive, (_, type) => type],
        (
            data,
            type
        ):
            | { status: 'loading' }
            | { status: 'error'; error: string }
            | {
                  status: 'success'
                  id: string | undefined
                  data: AccreditationDocument_v2
              } => {
            if (data.status === 'loading') return { status: 'loading' }
            if (data.status === 'error')
                return { status: 'error', error: data.error }

            const defaultDoc = accreditationDocumentSchema.parse({
                accreditationType: type,
            })
            try {
                const existingDoc = Object.entries(data.data || {})
                    .filter(([_, doc]) => doc.accreditationType === type)
                    .sort((a, b) => accreditationSort_v2(a[1], b[1]))?.[0]
                if (existingDoc) {
                    return {
                        status: 'success',
                        id: existingDoc[0],
                        data: accreditationDocumentSchema.parse(existingDoc[1]),
                    }
                }
                throw new Error()
            } catch (error) {
                return {
                    status: 'success',
                    id: undefined,
                    data: defaultDoc,
                }
            }
        }
    )

export const selectWorkingRightsType = createSelector(
    [selectTeacherWorkingRights],
    (
        data
    ):
        | { loading: true; data?: never }
        | { loading: false; data: WorkingRightsType | null } => {
        if (data.status !== 'success') return { loading: true }

        return {
            loading: false,
            data: data.data.value,
        }
    }
)

const selectAllAccreditationCheck = (state: RootState) =>
    state.teacher.onboardingChecks.accreditationAllReady

export const selectDerivedOnboardingState = createSelector(
    [
        selectDerivedBasicDetailsState,
        selectDerivedProfilePhotoState,
        selectTeacherWorkingRights,
        selectDerivedIdentityState,
        selectTeacherWorkingHistory,
        selectTeacherReference,
        selectDerivedEmergencyContactState,
        selectAllAccreditationCheck,
    ],
    (
        basicDetails,
        profilePhoto,
        workingRights,
        identity,
        workingHistory,
        reference,
        emergencyContacts,
        accreditationsReady
    ) => {
        if (
            basicDetails.loading ||
            profilePhoto.loading ||
            workingRights.status !== 'success' ||
            workingHistory.status !== 'success' ||
            identity.loading ||
            reference.status !== 'success' ||
            emergencyContacts.loading
        )
            return undefined

        return getCombinedState_v2([
            basicDetails.state,
            profilePhoto.state,
            workingRights.data.state,
            identity.state,
            workingHistory.data.state,
            reference.data.state,
            emergencyContacts.state,
            accreditationsReady ? 'submitted' : 'incomplete',
        ])
    }
)

export const selectDerivedOnboardingGroups = createSelector(
    [
        selectDerivedBasicDetailsState,
        selectDerivedProfilePhotoState,
        selectTeacherWorkingRights,
        selectDerivedIdentityState,
        selectTeacherWorkingHistory,
        selectTeacherReference,
        selectDerivedEmergencyContactState,
        selectAllAccreditationCheck,
    ],
    (
        basicDetails,
        profilePhoto,
        workingRights,
        identity,
        workingHistory,
        reference,
        emergencyContacts,
        accreditationsReady
    ) => {
        if (
            basicDetails.loading ||
            profilePhoto.loading ||
            workingRights.status !== 'success' ||
            workingHistory.status !== 'success' ||
            identity.loading ||
            reference.status !== 'success' ||
            emergencyContacts.loading
        )
            return undefined

        return [
            basicDetails.state,
            profilePhoto.state,
            workingRights.data.state,
            identity.state,
            workingHistory.data.state,
            reference.data.state,
            emergencyContacts.state,
            accreditationsReady ? 'submitted' : 'incomplete',
        ]
    }
)

export const selectTeacherAdditionalNeeds = createSelector(
    [
        (state: RootState) => ({
            status: state.teacher.data.profile.status,
            data: state.teacher.data.profile.data?.supportsAdditionalNeeds,
        }),
    ],
    (data) => data
)
export const selectTeacherFavouriteSubjects = createSelector(
    [
        (state: RootState) => ({
            status: state.teacher.data.profile.status,
            data: state.teacher.data.profile.data?.favouriteSubjects,
        }),
    ],
    (data) => data
)
export const selectPreferredLanguages = (state: RootState) =>
    state.teacher.data.languages

export const makeSelectTeacherProfileChild = <T extends unknown>() =>
    createSelector(
        [selectTeacherProfile, (_, key: keyof TeacherProfile) => key],
        (profile, key) => ({
            status: profile.status,
            data: profile.data?.[key] as T | null,
        })
    )
export const makeSelectEducatorProfileChild = <T extends unknown>() =>
    createSelector(
        [selectEducatorProfile, (_, key: keyof EducatorProfile) => key],
        (profile, key) => ({
            status: profile.status,
            data: profile.data?.[key] as T | null,
        })
    )

const selectTeacherESProfile = (state: RootState) =>
    state.teacher.data.supportStaffProfile
export const makeSelectTeacherESProfileChild = <T extends unknown>() =>
    createSelector(
        [selectTeacherESProfile, (_, key: keyof SupportStaffProfile) => key],
        (profile, key) => ({
            status: profile.status,
            data: profile.data?.[key] as T | null,
        })
    )

const deriveLiveDataStatus = (list: LiveDataStatus[]): LiveDataStatus => {
    if (list.includes('error')) return 'error'
    if (list.includes('loading')) return 'loading'
    if (list.includes('default')) return 'default'
    return 'success'
}

const selectTeacherProfile = (state: RootState) => state.teacher.data.profile
export const makeSelectTeacherProfile = () =>
    createSelector(
        [selectTeacherProfile, selectPreferredLanguages, selectTeacherType],
        (profile, languages, type) => {
            const status = deriveLiveDataStatus([
                profile.status,
                languages.status,
                type.status,
            ])

            const errors = {
                profile: profile.status === 'error' ? profile.error : undefined,
                languages:
                    languages.status === 'error' ? languages.error : undefined,
                type: type.status === 'error' ? type.error : undefined,
            }

            if (status === 'error') {
                return {
                    status,
                    error: [errors.profile, errors.languages, errors.type]
                        .filter(Boolean)
                        .join('\n'),
                    errors,
                }
            }

            if (status !== 'success') {
                return {
                    status,
                    data: undefined,
                    errors,
                }
            }

            return {
                status,
                data: {
                    profile: profile.data ?? null,
                    languages: languages.data ?? null,
                    type: type.data ?? null,
                },
                errors,
            }
        }
    )

const selectSupportStaffProfile = (state: RootState) =>
    state.teacher.data.supportStaffProfile
export const makeSelectSupportStaffProfile = () =>
    createSelector(
        [
            selectSupportStaffProfile,
            selectPreferredLanguages,
            selectTeacherType,
        ],
        (profile, languages, type) => {
            const status = deriveLiveDataStatus([
                profile.status,
                languages.status,
                type.status,
            ])

            const errors = {
                profile: profile.status === 'error' ? profile.error : undefined,
                languages:
                    languages.status === 'error' ? languages.error : undefined,
                type: type.status === 'error' ? type.error : undefined,
            }

            if (status === 'error') {
                return {
                    status,
                    error: [errors.profile, errors.languages, errors.type]
                        .filter(Boolean)
                        .join('\n'),
                    errors,
                }
            }

            if (status !== 'success') {
                return {
                    status,
                    data: undefined,
                    errors,
                }
            }

            return {
                status,
                data: {
                    profile: profile.data ?? null,
                    languages: languages.data ?? null,
                    type: type.data ?? null,
                },
                errors,
            }
        }
    )

const selectEducatorProfile = (state: RootState) =>
    state.teacher.data.educatorProfile
export const makeSelectEducatorProfile = () =>
    createSelector(
        [selectEducatorProfile, selectPreferredLanguages],
        (profile, languages) => {
            const status = deriveLiveDataStatus([
                profile.status,
                languages.status,
            ])

            const errors = {
                profile: profile.status === 'error' ? profile.error : undefined,
                languages:
                    languages.status === 'error' ? languages.error : undefined,
            }

            if (status === 'error') {
                return {
                    status,
                    error: [errors.profile, errors.languages]
                        .filter(Boolean)
                        .join('\n'),
                    errors,
                }
            }

            if (status !== 'success') {
                return {
                    status,
                    data: undefined,
                    errors,
                }
            }

            return {
                status,
                data: {
                    profile: profile.data ?? null,
                    languages: languages.data ?? null,
                },
                errors,
            }
        }
    )

export const makeSelectAboutMeByEmploymentType = (
    employmentType: TeacherEmploymentSetting
) => {
    const profileSelector =
        employmentType === 'school'
            ? selectTeacherProfile
            : employmentType === 'supportStaff'
              ? selectSupportStaffProfile
              : selectEducatorProfile

    return createSelector([profileSelector], (profile) => ({
        status: profile.status,
        data: profile.data?.about ?? null,
    }))
}
export const makeSelectPreferredYearsByEmploymentType = (
    employmentType: 'school' | 'supportStaff'
) => {
    const profileSelector =
        employmentType === 'school'
            ? selectTeacherProfile
            : selectSupportStaffProfile

    return createSelector([profileSelector], (profile) => ({
        status: profile.status,
        data: profile.data?.years ?? null,
    }))
}

export const {
    setTeacherDefault,
    setLoading,
    setData,
    setAccountState,
    setSetLocation,
    setEmploymentSettings,
    setWorkingRights,
    setAccreditationSnapshot,
    upsertAccreditationDoc,
    removeAccreditationDoc,
    setAccreditationCheckState,
    syncAccreditationSettings,
    resetAccreditationCheckState,
} = teacherSlice.actions

export default teacherSlice.reducer
